import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { money, errMsg } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Brand } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/**
 * Shared bill link (/j/:code). Anyone the host texts opens this, enters their
 * name once (credential-free), and lands in the SAME bill room to claim their
 * items and pay their share. If they already have a session on this device,
 * they auto-join and go straight to the bill.
 */
export default function JoinLink() {
  const { code } = useParams();
  const { user, loading, startAnonymous } = useAuth();
  const navigate = useNavigate();
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const joinedRef = useRef(false);

  useEffect(() => {
    api
      .get(`/guest/preview/${code}`)
      .then((r) => setPreview(r.data))
      .catch((e) => setError(errMsg(e, "This link is invalid or expired.")));
  }, [code]);

  const doJoin = async () => {
    if (joinedRef.current) return;
    joinedRef.current = true;
    try {
      const r = await api.post("/bills/join", { invite_code: code.toUpperCase() });
      const id = r.data?.id || r.data?.bill?.id;
      navigate(id ? `/bill/${id}` : "/", { replace: true });
    } catch (err) {
      joinedRef.current = false;
      toast.error(errMsg(err, "Couldn't open the bill"));
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!loading && user) doJoin();
  }, [loading, user]); // eslint-disable-line react-hooks/exhaustive-deps

  const start = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await startAnonymous(name);
      await doJoin();
    } catch (err) {
      toast.error(
        err.response?.status === 429
          ? "Too many new joins from this network right now — wait a minute and retry."
          : errMsg(err, "Could not join")
      );
      setBusy(false);
    }
  };

  const Card = ({ children }) => (
    <div className="min-h-screen grid place-items-center p-6">
      <div className="w-full max-w-md hp-glass-strong rounded-[2rem] p-8 hp-fade-in text-center">
        <div className="flex justify-center mb-7">
          <Brand size="lg" to={null} float />
        </div>
        {children}
      </div>
    </div>
  );

  if (error)
    return (
      <Card>
        <p className="text-muted-foreground">{error}</p>
      </Card>
    );

  // Already signed in on this device → auto-joining
  if (loading || user)
    return (
      <Card>
        <Loader2 className="animate-spin text-[#c084fc] mx-auto" />
        <p className="text-sm text-muted-foreground mt-3">Opening the bill…</p>
      </Card>
    );

  return (
    <Card>
      <p className="text-sm text-muted-foreground">
        {preview?.creator_name ? `${preview.creator_name} invited you to` : "You're invited to"}
      </p>
      <h1 className="text-2xl font-extrabold text-white mt-1">
        {preview?.name || "Split a bill"}
      </h1>
      {preview?.total != null && (
        <p className="text-sm text-muted-foreground mt-1">
          {money(preview.total)} total
        </p>
      )}

      <form onSubmit={start} className="mt-7 space-y-4">
        <Input
          data-testid="joinlink-name-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="What's your name?"
          autoFocus
          className="h-12 bg-white/5 border-white/10 text-white text-center text-lg"
        />
        <Button
          data-testid="joinlink-continue-btn"
          type="submit"
          disabled={busy || !name.trim()}
          className="hp-cta w-full h-12 rounded-xl font-bold text-white text-base gap-2 border-0"
        >
          {busy ? "Joining…" : "Join & claim my items"}
          {!busy && <ArrowRight size={18} />}
        </Button>
      </form>
      <p className="text-xs text-muted-foreground mt-5">
        No signup — just your name. You'll pick your items and pay your share.
      </p>
    </Card>
  );
}
